import cn from 'classnames';
import React, { useEffect, useState, Dispatch, useMemo } from 'react';

import * as CH from '@/components/CareerHistory/Form/Confirmation';
import BaseButton from '@/components/common/Button/BaseButton';
import { FormLabel, FormLayoutUnitDivider } from '@/components/common/Form';
import { TooltipMessageMatchbou } from '@/components/common/Tooltip/Matchbou';
import { ButtonIconButton } from '@/components/common/Button/IconButton';
import { FORM_OPTIONS } from '@/definition/FORM_OPTIONS';
import { Career } from '@/utils/api-client';

type TFieldProps = {
  index: number;
  item: Career;
  foldable?: boolean;
};

export const CareerOriginal: React.VFC<TFieldProps> = ({ item, index, foldable }) => {
  const [currentlyInOffice, setCurrentlyInOffice] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(true);

  useEffect(() => {
    setCurrentlyInOffice(!item.c_leave_flag);
  }, [item]);

  const employment_status = useMemo(() => {
    const type = FORM_OPTIONS.employment_contract_type.filter(
      (type) => type.value === String(item.c_employment_status)
    );
    return type.length ? type[0].children : '';
  }, [item]);

  return (
    <>
      <input type="hidden" value={item.c_id} />
      <div className="space-y-8 mb-22">
        <FormLabel label="会社名" type="small" />
        <div className="mkt_mask_items">
          <div className="mbx-formContainer--textfield-wrapper pointer-events-none opacity-40">
            <input
              type="text"
              className="mbx-formContainer mbx-formContainer--textfield"
              placeholder="例）株式会社マッチラボ"
              value={item.c_company_name ? item.c_company_name : ''}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* 在籍期間 */}
      <div className="space-y-8 mb-22">
        <FormLabel label="在籍期間" type="small" />
        <div>
          <label className="mbx-formCheckbox relative pointer-events-none opacity-40">
            <input type="checkbox" checked={currentlyInOffice} readOnly />
            <span className="text-body_1 py-12 pl-32">
              現在この会社で働いています<i className="mbx-icon mbx-icon-Check"></i>
            </span>
          </label>
        </div>
        <div className="lg:flex md:flex lg:space-x-8 md:space-x-8 sm:space-y-8">
          <div className="flex space-x-8">
            <div className="w-80 pointer-events-none opacity-40">
              <div className="mbx-formContainer--textfield-wrapper">
                <input
                  type="number"
                  className="mbx-formContainer mbx-formContainer--textfield"
                  placeholder="yyyy"
                  value={item.c_join_date ? item.c_join_date.split('-')[0] : ''}
                  readOnly
                />
              </div>
            </div>
            <FormLayoutUnitDivider>年</FormLayoutUnitDivider>
            <div className="w-80 pointer-events-none opacity-40">
              <div className="mbx-formContainer--textfield-wrapper">
                <input
                  type="number"
                  className="mbx-formContainer mbx-formContainer--textfield"
                  placeholder="mm"
                  value={item.c_join_date ? item.c_join_date.split('-')[1] : ''}
                  readOnly
                />
              </div>
            </div>
            <FormLayoutUnitDivider>
              <>月 〜 {currentlyInOffice && <> 現在</>}</>
            </FormLayoutUnitDivider>
          </div>

          {!currentlyInOffice && (
            <div className="flex space-x-8">
              <div className="w-80 pointer-events-none opacity-40">
                <div className="mbx-formContainer--textfield-wrapper">
                  <input
                    type="number"
                    className="mbx-formContainer mbx-formContainer--textfield"
                    placeholder="yyyy"
                    value={item.c_leave_date ? item.c_leave_date.split('-')[0] : ''}
                    readOnly
                  />
                </div>
              </div>
              <FormLayoutUnitDivider>年</FormLayoutUnitDivider>
              <div className="w-80 pointer-events-none opacity-40">
                <div className="mbx-formContainer--textfield-wrapper">
                  <input
                    type="number"
                    className="mbx-formContainer mbx-formContainer--textfield"
                    placeholder="mm"
                    value={item.c_leave_date ? item.c_leave_date.split('-')[1] : ''}
                    readOnly
                  />
                </div>
              </div>
              <FormLayoutUnitDivider>月</FormLayoutUnitDivider>
            </div>
          )}
        </div>
      </div>

      <div className={cn({ hidden: foldable && !open })}>
        <div className="space-y-8 mb-22">
          <FormLabel label="部署名" type="small" />
          <div className="mkt_mask_items pointer-events-none opacity-40">
            <div className="mbx-formContainer--textfield-wrapper">
              <input
                type="text"
                className="mbx-formContainer mbx-formContainer--textfield"
                placeholder="例）サービスデザイン事業部"
                value={item.c_section ? item.c_section : ''}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="space-y-8 mb-22">
          <FormLabel label="役職名" type="small" />
          <div className="mkt_mask_items pointer-events-none opacity-40">
            <div className="mbx-formContainer--textfield-wrapper">
              <input
                type="text"
                className="mbx-formContainer mbx-formContainer--textfield"
                placeholder="例）チーフマネージャー"
                value={item.c_post ? item.c_post : ''}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="space-y-8 mb-22">
          <FormLabel label="事業内容" type="small" />
          <div className="mkt_mask_items pointer-events-none opacity-40">
            <div className="mbx-formContainer--textfield-wrapper">
              <input
                type="text"
                className="mbx-formContainer mbx-formContainer--textfield"
                placeholder="例）Webサイト開発、アプリ開発、サービスデザイン支援"
                value={item.c_detail ? item.c_detail : ''}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="space-y-8 mb-22">
          <FormLabel label="従業員数" type="small" />
          <div className="flex space-x-8">
            <div className="w-150 mkt_mask_items pointer-events-none opacity-40">
              <div className="mbx-formContainer--textfield-wrapper">
                <input
                  type="number"
                  className="mbx-formContainer mbx-formContainer--textfield"
                  placeholder="例）1000"
                  value={item.c_employee_count ? item.c_employee_count : ''}
                  readOnly
                />
              </div>
            </div>
            <FormLayoutUnitDivider className="w-auto">名</FormLayoutUnitDivider>
          </div>
        </div>
        <div className="space-y-8 mb-22">
          <FormLabel label="資本金" type="small" />
          <div className="flex space-x-8">
            <div className="w-150 mkt_mask_items pointer-events-none opacity-40">
              <div className="mbx-formContainer--textfield-wrapper">
                <input
                  type="number"
                  className="mbx-formContainer mbx-formContainer--textfield"
                  placeholder="例）25000"
                  value={item.c_capital ? item.c_capital : ''}
                  readOnly
                />
              </div>
            </div>
            <FormLayoutUnitDivider className="w-auto">万円</FormLayoutUnitDivider>
          </div>
        </div>
        <div className="space-y-8 mb-22">
          <FormLabel label="雇用形態" type="small" />
          <div className="mbx-formContainer--textfield-wrapper pointer-events-none opacity-40">
            <input
              type="text"
              className="mbx-formContainer mbx-formContainer--textfield"
              placeholder="選択してください"
              value={employment_status}
              readOnly
            />
          </div>
        </div>

        <CH.ProjectsOriginal index={index} project={item.c_projects} />

        <div className="space-y-8 mb-22">
          <FormLabel label="転職・退職理由" type="small"></FormLabel>
          <div className="mkt_mask_items pointer-events-none opacity-40">
            <div className="mbx-formContainer mbx-formContainer--textarea min-h-48 bg-white">
              {item.c_leave_reason ? (
                item.c_leave_reason
              ) : (
                <p className="opacity-50">例）一身上の都合により退職。</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {foldable && (
        <BaseButton
          iconName="Arrow_Down"
          size="m"
          theme="tertiary"
          className={cn('toggle-button', { open })}
          onClick={() => setOpen(!open)}
          type="button"
        >
          {open ? '職歴を閉じる' : '詳細を入力する'}
        </BaseButton>
      )}
    </>
  );
};
